import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpenIcon, UsersIcon, DollarSignIcon, TrendingUpIcon, PackageIcon, CalendarIcon, ArrowRightIcon, BarChart2Icon } from 'lucide-react';
import BackButton from '../../components/UI/BackButton';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
const VendorAnalytics = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const analyticsCategories = [{
    title: 'Book & Audiobook Analytics',
    description: 'Track downloads, read depth, completion rates, and reader engagement across your titles.',
    icon: BookOpenIcon,
    link: '/analytics',
    color: 'bg-purple-100 text-purple-600',
    darkColor: 'bg-purple-900/30 text-purple-300'
  }, {
    title: 'Reader Insights',
    description: 'Understand your audience with demographics, geographic distribution, and reading behavior.',
    icon: UsersIcon,
    link: '/vendors/insights',
    color: 'bg-blue-100 text-blue-600',
    darkColor: 'bg-blue-900/30 text-blue-300'
  }, {
    title: 'Revenue & Royalties',
    description: 'View earnings breakdown, payout history, and future revenue projections.',
    icon: DollarSignIcon,
    link: '/vendors/royalties',
    color: 'bg-green-100 text-green-600',
    darkColor: 'bg-green-900/30 text-green-300'
  }, {
    title: 'Marketing Performance',
    description: 'Analyze campaign results, promo code usage, and social media reach.',
    icon: TrendingUpIcon,
    link: '/vendors/marketing',
    color: 'bg-orange-100 text-orange-600',
    darkColor: 'bg-orange-900/30 text-orange-300'
  }, {
    title: 'Pre-Orders & ARCs',
    description: 'Track pre-order conversions and manage ARC reader statistics.',
    icon: PackageIcon,
    link: '/vendors/preorders',
    color: 'bg-rose-100 text-rose-600',
    darkColor: 'bg-rose-900/30 text-rose-300'
  }, {
    title: 'Event Performance',
    description: 'Monitor ticket sales, attendance numbers, and event reach.',
    icon: CalendarIcon,
    link: '/vendors/events',
    color: 'bg-indigo-100 text-indigo-600',
    darkColor: 'bg-indigo-900/30 text-indigo-300'
  }];
  return <div className={`min-h-screen py-8 ${dk.page}`} data-id="element-22314" data-mp="27c9tbz4u1fuh">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22315" data-mp="22dyknrq9xbdu">
        <div className="mb-6" data-id="element-22316" data-mp="22b2l5yys2ena">
          <BackButton data-id="element-22317" data-mp="1xdubkmrp3ixn" />
        </div>

        <div className="mb-8" data-id="element-22318" data-mp="1xcspzod1ze1o">
          <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-22319" data-mp="1xgow0eapl3r2">
            Analytics & Insights
          </h1>
          <p className={`mt-2 text-lg ${dk.textSecondary}`} data-id="element-22320" data-mp="1odxhg5mjdrv7">
            Track performance across all your listings and understand your
            audience.
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10" data-id="element-22321" data-mp="3ir5f051wchkj">
          {[{
          label: 'Total Revenue',
          value: '$18,750',
          change: '+14%',
          icon: DollarSignIcon
        }, {
          label: 'Total Sales',
          value: '1,245',
          change: '+8%',
          icon: PackageIcon
        }, {
          label: 'Total Readers',
          value: '3.8k',
          change: '+22%',
          icon: UsersIcon
        }, {
          label: 'Avg Rating',
          value: '4.8',
          change: '+0.2',
          icon: BarChart2Icon
        }].map((stat, index) => <div key={index} className={`p-6 rounded-xl shadow-sm border ${dk.card}`} data-id="element-22322" data-mp="3lc4urs4btnq3" data-mpi={index}>
              <div className="flex justify-between items-start mb-4" data-id="element-22323" data-mp="wl2bn9g6i6sl">
                <div className={`p-2 rounded-lg ${dk.isDark ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-22324" data-mp="10lvx7w65f9ib">
                  <stat.icon className={`h-5 w-5 ${dk.textPrimary}`} data-id="element-22325" data-mp="z3xddlqzwxk5" />
                </div>
                <span className={`text-sm font-medium ${stat.change.startsWith('+') ? 'text-green-600' : 'text-red-600'}`} data-id="element-22326" data-mp="u6fbgfbebiln">
                  {stat.change}
                </span>
              </div>
              <h3 className={`text-2xl font-bold ${dk.textPrimary}`} data-id="element-22327" data-mp="1428m1t5k120s">
                {stat.value}
              </h3>
              <p className={`text-sm ${dk.textMuted}`} data-id="element-22328" data-mp="1i1i12pi0xq36">{stat.label}</p>
            </div>)}
        </div>

        {/* Analytics Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-22329" data-mp="1rscnpuz6mrp5">
          {analyticsCategories.map((category, index) => <Link key={index} to={category.link} className={`group p-6 rounded-xl shadow-sm border transition-all hover:shadow-md ${dk.card}`} data-id="element-22330" data-mp="1mwe5yq8d07xp" data-mpi={index}>
              <div className="flex items-start justify-between mb-4" data-id="element-22331" data-mp="1wqe6sfwlyr0m">
                <div className={`p-3 rounded-full ${dk.isDark ? category.darkColor : category.color}`} data-id="element-22332" data-mp="2fi2q7on06fuz">
                  <category.icon className="h-6 w-6" data-id="element-22333" data-mp="2kdi24b1ga1or" />
                </div>
                <div className={`p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity ${dk.isDark ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-22334" data-mp="26ftit7n172lt">
                  <ArrowRightIcon className={`h-4 w-4 ${dk.textSecondary}`} data-id="element-22335" data-mp="1woqhc4q6wlm0" />
                </div>
              </div>
              <h3 className={`text-xl font-bold mb-2 ${dk.textHeading}`} data-id="element-22336" data-mp="21ib9vh90kndt">
                {category.title}
              </h3>
              <p className={`mb-4 ${dk.textSecondary}`} data-id="element-22337" data-mp="1rr6u4fbikyhx">
                {category.description}
              </p>
              <span className={`inline-flex items-center text-sm font-medium ${rt.accentLink}`} data-id="element-22338" data-mp="1dqw053ewr3sj">
                View Details <ArrowRightIcon className="ml-1 h-3 w-3" data-id="element-22339" data-mp="1ipz7wvm9vo47" />
              </span>
            </Link>)}
        </div>
      </div>
    </div>;
};
export default VendorAnalytics;